class DniproCatalog extends HTMLElement {
  connectedCallback() {
    if (this.abort) return;
    this.cards = [...this.querySelectorAll('[data-card]')];
    if (!this.cards.length) return;
    this.abort = new AbortController();
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.active = 0;
    this.mode = 'cards';
    this.stage = this.querySelector('[data-stage]');
    this.deck = this.querySelector('[data-deck]');
    this.openLink = this.querySelector('[data-open]');
    const progress = this.querySelector('[data-progress]');
    progress.replaceChildren();
    this.dots = this.cards.map((card, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.setAttribute('aria-label', `${index + 1}. ${card.dataset.title}`);
      dot.dataset.index = index;
      progress.append(dot);
      return dot;
    });
    this.listen(this, 'click', event => this.click(event));
    this.listen(this, 'keydown', event => {
      if (this.mode !== 'cards' || event.altKey || event.ctrlKey || event.metaKey || /INPUT|TEXTAREA|SELECT/.test(event.target.tagName)) return;
      const index = {ArrowLeft:this.active - 1, ArrowRight:this.active + 1, Home:0, End:this.cards.length - 1}[event.key];
      if (index === undefined) return;
      event.preventDefault();
      const focusCard = event.target.matches('[data-select]');
      this.go(index);
      if (focusCard) this.cards[this.active].querySelector('[data-select]').focus({preventScroll:true});
    });
    this.listen(this.deck, 'pointerdown', event => {
      if (event.button !== 0 || this.mode !== 'cards') return;
      this.pointer = {id:event.pointerId,x:event.clientX,y:event.clientY};
      event.target.setPointerCapture?.(event.pointerId);
    });
    this.listen(this.deck, 'pointerup', event => {
      if (!this.pointer || this.pointer.id !== event.pointerId) return;
      const dx = event.clientX - this.pointer.x, dy = event.clientY - this.pointer.y;
      this.pointer = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.2) {
        this.suppressUntil = performance.now() + 400;
        this.go(this.active + (dx < 0 ? 1 : -1));
      }
    });
    this.listen(this.deck, 'pointercancel', () => this.pointer = null);
    this.listen(document, 'shopify:block:select', event => {
      const index = this.cards.findIndex(card => card.dataset.blockId === event.detail.blockId);
      if (index < 0) return;
      this.setMode('cards');this.go(index);
    });
    this.listen(window, 'pageshow', () => {delete this.dataset.opening;this.busy = false;});
    this.listen(document, 'visibilitychange', () => this.toggleAttribute('data-paused', document.hidden));
    this.resize = new ResizeObserver(() => this.position());
    this.resize.observe(this.stage);
    this.intersection = new IntersectionObserver(([entry]) => this.toggleAttribute('data-paused', !entry.isIntersecting || document.hidden));
    this.intersection.observe(this);
    this.setMode('cards', false);
    if (!this.motion.matches) {
      this.deck.classList.add('is-entering');
      this.introTimer = setTimeout(() => this.deck.classList.remove('is-entering'), 650);
    }
  }
  listen(target, type, callback) {target.addEventListener(type, callback, {signal:this.abort.signal});}
  disconnectedCallback() {
    this.abort?.abort();this.abort = null;this.resize?.disconnect();this.intersection?.disconnect();
    clearTimeout(this.timer);clearTimeout(this.introTimer);
  }
  go(index) {
    if (this.busy || this.mode !== 'cards') return;
    this.active = (index + this.cards.length) % this.cards.length;
    this.render(true);
  }
  position() {
    if (this.mode !== 'cards') return;
    const width = this.stage.getBoundingClientRect().width;
    const step = width < 601 ? Math.min(148,width*.38) : width < 901 ? 185 : 235;
    this.cards.forEach((card,index) => {
      let distance = (index - this.active + this.cards.length) % this.cards.length;
      if (distance > this.cards.length/2) distance -= this.cards.length;
      const offset = Math.abs(distance), visible = offset <= 2;
      card.style.setProperty('--card-transform',`translate(-50%,-50%) translateX(${distance*step}px) translateY(${offset*11}px) rotate(${distance*13}deg) scale(${offset===0?1:offset===1?.83:.66})`);
      card.style.setProperty('--card-opacity', visible ? offset===0 ? '1' : offset===1 ? '.74' : '.40' : '0');
      card.style.setProperty('--card-z',30-offset);
      card.classList.toggle('is-away',!visible);
      card.inert = !visible;
      card.setAttribute('aria-hidden',String(!visible));
    });
  }
  render(announce = false) {
    const item = this.cards[this.active].dataset;
    this.cards.forEach((card,index) => {
      card.classList.toggle('is-current',index===this.active);
      const button = card.querySelector('[data-select]');
      button.tabIndex = index===this.active ? 0 : -1;
      button.setAttribute('aria-pressed',String(index===this.active));
    });
    const count = `${String(this.active+1).padStart(2,'0')} / ${String(this.cards.length).padStart(2,'0')}`;
    this.querySelector('[data-count]').textContent = count;
    this.querySelector('[data-info] [data-title]').textContent = item.title;
    this.querySelector('[data-info] [data-description]').textContent = item.description;
    this.openLink.querySelector('[data-open-label]').textContent = item.url ? (item.cta || 'Explorar categoría') : 'Próximamente';
    this.openLink.querySelector('[data-open-icon]').hidden = !item.url;
    if (item.url) {this.openLink.setAttribute('href',item.url);this.openLink.removeAttribute('aria-disabled');this.openLink.removeAttribute('tabindex');}
    else {this.openLink.removeAttribute('href');this.openLink.setAttribute('aria-disabled','true');this.openLink.tabIndex = -1;}
    this.dots.forEach((dot,index) => dot.setAttribute('aria-current',String(index===this.active)));
    this.querySelector('[data-prev]').disabled = this.cards.length < 2;
    this.querySelector('[data-next]').disabled = this.cards.length < 2;
    if (announce) this.querySelector('[data-announcement]').textContent = `${item.title}. ${count}${item.url?'':'. Próximamente'}`;
    this.position();
  }
  setMode(mode, focus = true) {
    if (this.busy) return;
    this.mode = mode;this.dataset.mode = mode;
    const cards = mode==='cards';
    for (const selector of ['[data-info]','[data-controls]','[data-prev]','[data-next]']) this.querySelector(selector).hidden = !cards;
    this.querySelector('[data-cards-mode]').hidden = cards;
    for (const card of this.cards) {
      card.querySelector('[data-select]').hidden = !cards;
      if (!cards) {card.inert=false;card.removeAttribute('aria-hidden');}
    }
    if (cards) this.render();
    if (focus) (cards ? this.cards[this.active].querySelector('[data-select]') : this.querySelector('[data-cards-mode]')).focus({preventScroll:true});
  }
  click(event) {
    const target = event.target.closest('button,a');
    if (!target || !this.contains(target)) return;
    if (target.matches('[data-prev]')) return this.go(this.active-1);
    if (target.matches('[data-next]')) return this.go(this.active+1);
    if (target.matches('[data-index]')) return this.go(Number(target.dataset.index));
    if (target.matches('[data-classic-mode]')) return this.setMode('classic');
    if (target.matches('[data-cards-mode]')) return this.setMode('cards');
    if (target.matches('[data-select]')) {
      if (performance.now() < (this.suppressUntil||0)) return;
      const index = this.cards.indexOf(target.closest('[data-card]'));
      return index===this.active ? this.open(event) : this.go(index);
    }
    if (target.matches('[data-open]')) this.open(event);
  }
  open(event) {
    const url = this.cards[this.active].dataset.url;
    if (!url) {event.preventDefault();return;}
    // Preserve browser modifiers on real links. Normal activation always uses this tab.
    if (event.target.closest('a') && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0)) return;
    event.preventDefault();
    if (this.busy) return;
    this.busy = true;this.dataset.opening = 'true';
    const navigate = () => window.location.assign(url);
    if (this.motion.matches) navigate();else this.timer = setTimeout(navigate,320);
  }
}
if (!customElements.get('dm-interactive-catalog')) customElements.define('dm-interactive-catalog',DniproCatalog);
